import {useEffect, useRef, useState} from "react";
import {useSearchParams} from "react-router";
import {Box, Chip} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import {
    fetchJobTapeData,
    fetchJobTapeDataWithCount,
    fetchJobTapeStatusCount,
    invalidateJobTapeListCache,
    JOB_TAPE_PAGE_SIZE,
} from "../../utils/dashboardCalls";
import {useStyles} from "../../utils/uiUtils";
import DataTable from "../DataTable/DataTable";
import {MediaMetrics} from "../widgets/MediaMetrics/MediaMetrics";
import {MEDIA_FILTER_PARAM} from "../widgets/MediaMetrics/mediaFilterNavigation";
import {mediaFilterChipText, mediaFilterMatches, mediaFilterRequestOptions, resolveMediaFilter} from "../widgets/MediaMetrics/mediaMetricsDataRetrieving";
import {TAPE_INGESTION_TABLE_COLUMNS} from "./ingestionTableCustomization";
import {TapeDetails} from "../../views/TapeDetails/tapeDetails";
import {addClickCallback} from "../DataTable/tableUtils";
import {useAuth} from "../../views/AuthProvider/AuthProvider";
import {TAPE_AUDIT_COMPLETE_STATUS_IDS, TAPE_INGESTION_COMPLETE_STATUS_IDS} from "../../views/Admin/Job/JobManagement/Processing/processingConfig";

/**
 * How often the first page of the tape table and the counters above it are re-read.
 *
 * Thirty seconds, matched to what is actually being watched: reading a tape takes minutes, so the events
 * this page exists to show — a read starting, a read finishing, a tape being catalogued — arrive minutes
 * apart. At ten seconds, the interval this started on, roughly five ticks in six had nothing to report,
 * and the three requests it now makes an operator's session cost per minute were thirteen.
 *
 * The ordering is what makes any interval worth having — the list comes back most-recently-modified
 * first (see JOB_TAPE_ORDER_BY), so whatever changed since the last tick is on the page being refreshed.
 *
 * The cost is one `man_item_list` and, from the card, twelve `man_item_count` queries per tick, all
 * narrowed to a single job of a single company. That is a page an operator has deliberately opened,
 * not something every logged-in session pays for; the dashboard, which counts across every company,
 * stays on its own slower interval.
 */
export const TAPE_REFRESH_INTERVAL_MS = 30 * 1000;

/**
 * Shared tape processing view — shows the Media Metrics card for the job and the tape DataTable.
 * Safe for both admin and customer contexts.
 *
 * The table can be narrowed to the media behind one of the card's counters, by a `?media=<key>` query
 * parameter that clicking a tile puts there. Filtered, the table shows a chip naming the criteria and the
 * whole-job status derivation is suppressed — see loadData for why that matters.
 *
 * @param {object} orderDetail - The order detail object (must have tapeark_job_id; `uuid` lets a clicked
 *        tile link to this page without looking the order up)
 * @param {number} cid - The company ID for SaaS API routing.
 *                       Admin: pass customer's arkbridge_customer_id.
 *                       Customer: derive from useAuth() (isAdmin ? 0 : user.arkbridge_customer_id).
 * @param {function} [onDataLoaded] - Optional callback fired after first data load: ({entriesCount, auditedCount, completedCount}) => void
 *                                    Used by admin wrapper to trigger auto-status derivation.
 * @param showHeading
 */
export const TapeProcessingView = ({orderDetail, cid, onDataLoaded, showHeading = false}) => {
    const classes = useStyles();
    const {features} = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();

    const [tapeData, setTapeData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [entriesCount, setEntriesCount] = useState(0);
    const [selectedTapeId, setSelectedTapeId] = useState();
    const [openTapeDetails, setOpenTapeDetails] = useState(false);

    const jobId = orderDetail?.tapeark_job_id;

    /*
     * The table's filter, read off the URL rather than held in state.
     *
     * Set by clicking a Media Metrics tile above, which navigates here with `?media=<key>`. Keeping it in
     * the query string is what makes the filtered table a place: it survives a reload, it can be pasted to
     * a colleague, and arriving cold from a link shows the same rows as arriving by a click. State would
     * give none of that, and would also have to be reset by hand every time the job changed.
     *
     * An unrecognised key resolves to undefined and the table is simply unfiltered — a stale bookmark
     * should show the job, not an error.
     */
    const mediaFilter = resolveMediaFilter(searchParams.get(MEDIA_FILTER_PARAM));
    const mediaFilterKey = mediaFilter?.key ?? '';
    // The three fetch paths all narrow by this. `{}` when there is no filter, which keeps every request
    // byte-identical to what it was before this feature existed, cache keys included.
    const filterOptions = mediaFilterRequestOptions(mediaFilter);

    /**
     * Narrows the table to a tile's filter, without leaving the page.
     *
     * The card above the table is the same card the dashboard shows, and its tiles mean the same thing in
     * both places; the only difference is that here the table they describe is already on screen, so there is
     * nothing to navigate to. Written against the descriptor rather than against In Library, so every tile
     * that becomes clickable later arrives here needing no change.
     *
     * Still recorded in the URL — the reader can reload, bookmark or send the filtered table on, exactly as
     * they can when they arrive on the link from the dashboard, and the fetch effects already watch this.
     *
     * @param {object} filter - A MEDIA_FILTERS descriptor.
     */
    const applyMediaFilter = (filter) => {
        const next = new URLSearchParams(searchParams);
        next.set(MEDIA_FILTER_PARAM, filter.key);
        // A history entry rather than a replacement: the reader chose this, so Back is the way out of it.
        setSearchParams(next);
    };

    /** Drops the filter, leaving the reader on the same page with the whole job listed. */
    const clearMediaFilter = () => {
        const next = new URLSearchParams(searchParams);
        next.delete(MEDIA_FILTER_PARAM);
        // `replace` so Back returns to wherever the reader came from rather than to the filtered table
        // they have just dismissed.
        setSearchParams(next, {replace: true});
    };

    /*
     * What page one looked like the last time it was read, so a refresh can tell whether anything
     * actually moved. A ref rather than state: nothing renders it, and it must not itself cause the
     * render that would schedule the next read.
     */
    const firstPageSignature = useRef(null);

    /**
     * Identity and status of the rows on page one, plus the total.
     *
     * Enough to notice the two things that invalidate the pages behind it — a tape changing status, and
     * the row order changing because one did. Not the whole row: data volume and comments can be
     * rewritten without moving anything, and re-reading four cached pages for an edited note would be
     * paying ArkBridge for nothing.
     */
    const pageSignature = (count, rows) => `${count}|${rows.map(row => `${row.item_id}:${row.status_id}`).join(',')}`;

    /*
     * What page one looked like on screen, as opposed to what it meant for the pages behind it.
     *
     * Derived from the column config rather than listed by hand, so a column added to the table is a field
     * this notices by itself. `status_id` is in there because the Status cell is rendered from the id rather
     * than the API's status string, and `item_id` because it is the row key.
     */
    const renderedFields = [
        'item_id',
        'status_id',
        ...TAPE_INGESTION_TABLE_COLUMNS["Standard tapes"].map(column => column.mnemonic),
    ];

    /**
     * Everything the table paints, so a refresh can tell whether there is anything new to show.
     *
     * Deliberately wider than pageSignature, which answers a different question: that one decides whether the
     * cached pages behind page one are still consistent, and an edited note moves nothing, so paying
     * ArkBridge to re-read four pages for it would be waste. This one decides whether to re-render at all, and
     * an edited note is visible, so it counts.
     */
    // Unit and record separators, so no combination of field values can spell another row's signature:
    // a comment ending in a comma must not be able to read as the start of the next field.
    const renderSignature = (count, rows) => `${count}|${rows
        .map(row => renderedFields.map(field => row[field]).join('\u001f'))
        .join('\u001e')}`;

    /** What the table is currently showing, so an unchanged answer can be dropped without touching state. */
    const renderedSignature = useRef(null);

    /**
     * Re-applies the filter to what came back, as a check on the SaaS rather than as the filter.
     *
     * `man_item_list` gained the two Metadata phrase parameters in the same change as this view; a SaaS
     * older than that ignores an unrecognised parameter instead of rejecting it, so the request succeeds
     * with the filter silently dropped and every tape in the job arrives. Left alone, the table would then
     * list media directly under a chip saying that media is excluded. Re-testing each row makes the worst
     * case a short page instead of a wrong one — see mediaFilterMatches, which is the same three tests the
     * SQL applies.
     *
     * Nothing is dropped when the SaaS has the parameters, so this costs a predicate per row and no
     * requests. It does not correct the total, which comes from man_item_count: that endpoint has had both
     * parameters all along, so a short page under a larger total is exactly the signal that the list
     * endpoint is behind.
     */
    const applyFilterToRows = (rows) => (mediaFilter ? rows.filter(row => mediaFilterMatches(mediaFilter, row)) : rows);

    /**
     * The rows in a stable order for display, by volser, rather than in the order the SaaS returned them.
     *
     * The list arrives most-recently-modified first, which is the right thing to ask for — it is what puts
     * whatever just happened on page one — and the wrong thing to show. Read out in that order, a tape
     * whose status changes jumps to the top of the table and pushes every row below it down a place, so the
     * one thing the operator was watching moves at the moment it changes and everything they were not
     * watching moves with it. Sorted by volser, the same tape stays on the line it was on and only its
     * Status cell changes: the row they are looking at is still under their cursor, and their place in the
     * page is not lost every time the job progresses.
     *
     * Volser rather than item_id because that is the identifier printed on the cartridge and shown in the
     * first column, so the table reads in the order an operator would sort a shelf. Numeric collation, so
     * ABC9 comes before ABC10; item_id breaks a tie, and stands in for a row with no volser at all, so the
     * order is total and a re-read of the same page cannot produce two different orders.
     *
     * What this does not do is change which rows are on page one — that is the server's ordering, and a
     * tape modified while the operator is on page two of a filtered table still arrives on page one and
     * displaces something. Fixing that needs a stable ORDER BY on `man_item_list` itself.
     *
     * Copied before sorting: the array belongs to the response, and the signatures below are taken from it
     * in the order it arrived.
     */
    const orderRowsForDisplay = (rows) => [...rows].sort((left, right) => (
        String(left.item_volser ?? '').localeCompare(String(right.item_volser ?? ''), undefined, {numeric: true, sensitivity: 'base'})
        || (left.item_id ?? 0) - (right.item_id ?? 0)
    ));

    // Re-runs when the filter changes, so clicking a tile — or clearing the chip — reloads the table
    // rather than leaving the previous rows under the new heading.
    useEffect(() => {
        if (features.length > 0 && cid && jobId) loadData();
        // loadData closes over (cid, jobId, mediaFilterKey via filterOptions), all listed.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [features, cid, jobId, mediaFilterKey]);

    /*
     * The periodic refresh, and only ever of the first page.
     *
     * First page only because that is where the movement is: the list is ordered
     * most-recently-modified first, so a tape whose status changes moves onto page one, and re-reading
     * page four would be watching the part of the job that is finished. It also means an operator who
     * has paged away is left alone — a table that reshuffled itself under a click would be worse than a
     * stale one — which is why the interval is torn down and rebuilt as `currentPage` changes rather
     * than the tick quietly resetting the page to one.
     *
     * Four deliberate details:
     *   - `background: true`, which keeps the request out of the global loading wash the axios
     *     interceptor puts over the window while anything is in flight (see index.js). Unmarked, every
     *     tick washed the entire page out white for as long as the request took — reported as a white
     *     flash on every tick, and read as the page reloading itself rather than refreshing a row.
     *     Reads a reader is waiting for, including the first load, are left to raise it as before.
     *   - `cache: false`, because postDashboard caches man_item_list for ten minutes and the poll would
     *     otherwise re-ask the same question and be handed the same answer for the whole of it. Every
     *     read of page one bypasses the cache for the same reason, including paging back to it.
     *   - nothing is set on failure, so one dropped request leaves the last good page on screen instead
     *     of emptying the table.
     *   - `onDataLoaded` is not fired. It drives the admin view's automatic order-status derivation,
     *     which PATCHes the job when the derived status differs; running that off the timer would turn
     *     one write into a write every half minute for as long as the tab stayed open. Deriving status
     *     stays a load-time job.
     */
    const refreshFirstPage = (signal) => {
        fetchJobTapeDataWithCount(cid, jobId, 1, {cache: false, background: true, signal, ...filterOptions})
            .then(({rows: listed, count}) => {
                const rows = applyFilterToRows(listed);
                const entryCount = Number.isFinite(count) ? count : rows.length;

                /*
                 * Page one moved, so the pages behind it are no longer a consistent view of the same
                 * list: ordered by last-modified, a tape that just changed has left whatever page it was
                 * on and pushed the rows below it down a place, which a page cached before that shows as
                 * a repeated or a missing row. Dropping them costs a query only when something actually
                 * changed, and only for whoever pages back.
                 *
                 * Taken from the rows as the SaaS ordered them, and asked before the paint check below,
                 * because the two are separate questions: this one is about the list the cached pages were
                 * cut out of, and the display order the table now imposes says nothing about it. A tick can
                 * quite properly find the cached pages stale and the visible page unchanged — two rows on
                 * page one swapping places under last-modified is exactly that — and each answer has to
                 * stand whichever way the other one goes.
                 */
                const signature = pageSignature(entryCount, rows);
                if (firstPageSignature.current !== null && firstPageSignature.current !== signature) {
                    invalidateJobTapeListCache(cid, jobId);
                }
                firstPageSignature.current = signature;

                /*
                 * A tick that found nothing new stops here, before it can touch state.
                 *
                 * Most ticks are this one: an operator watches a job for minutes at a time and a tape changes
                 * every so often, not every half minute. Setting the same rows again would re-render the
                 * table, and while React writes nothing to the DOM when the output matches, the render is
                 * still a hundred-odd cells rebuilt, every tape icon's data URI rebuilt with them, and the
                 * MUI styling recomputed — for a table that by definition has nothing to say. Returning is
                 * both cheaper and provably invisible: with no state change there is no render to see.
                 *
                 * Measured on the display order, so this asks the only question worth asking — whether what
                 * the reader can see has changed — and a reshuffle they would not have seen is not a reason
                 * to redraw the page under them.
                 */
                const display = orderRowsForDisplay(rows);
                const rendered = renderSignature(entryCount, display);
                if (renderedSignature.current === rendered) return;
                renderedSignature.current = rendered;

                setTapeData(display);
                setEntriesCount(entryCount);
                setTotalPages(Math.ceil(entryCount / JOB_TAPE_PAGE_SIZE));
            })
            .catch(error => {
                if (error?.code === 'ERR_CANCELED') return;
                console.error("Error refreshing first page of tape data:", error);
            });
    };

    useEffect(() => {
        if (!(features.length > 0 && cid && jobId)) return;
        if (currentPage !== 1) return;

        const controller = new AbortController();
        const timer = setInterval(() => {
            // A tab behind other windows is not being watched, so its queries buy nothing. The next
            // tick that lands while visible picks the table back up.
            if (document.hidden) return;
            refreshFirstPage(controller.signal);
        }, TAPE_REFRESH_INTERVAL_MS);

        return () => {
            clearInterval(timer);
            controller.abort();
        };
        // refreshFirstPage is redefined every render; the scope it closes over is (cid, jobId, the
        // filter), all listed.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [features, cid, jobId, currentPage, mediaFilterKey]);

    const loadData = () => {
        fetchJobTapeDataWithCount(cid, jobId, 1, {cache: false, ...filterOptions}).then(({rows: listed, count}) => {
            const rows = applyFilterToRows(listed);
            const display = orderRowsForDisplay(rows);
            setTapeData(display);
            const entryCount = Number.isFinite(count) ? count : rows.length;
            setEntriesCount(entryCount);
            setTotalPages(Math.ceil(entryCount / JOB_TAPE_PAGE_SIZE));
            setCurrentPage(1);
            // The baselines the refresh compares against, recorded rather than compared: this runs on
            // mount and whenever the job changes, so there is nothing before it to have moved. Each is
            // taken from the order its own check uses — the SaaS's for the cached pages, the display's
            // for the paint.
            firstPageSignature.current = pageSignature(entryCount, rows);
            renderedSignature.current = renderSignature(entryCount, display);
            // One count per group rather than one per status: a tape holds exactly one status, so
            // count(StatusID in group) is the same number as the sum of the per-status counts, in
            // two requests instead of twelve.
            //
            // Not held in state any more, and not rendered here: these two counts used to feed the Audit
            // and Ingestion progress bars, which the metrics card has replaced. They are still fetched
            // because `onDataLoaded` is what drives the admin view's automatic status derivation — the
            // figures leave this component rather than being displayed by it.
            //
            // Unfiltered, deliberately, and skipped altogether while a filter is applied.
            //
            // What consumes this is a statement about the whole job: Processing.jsx compares
            // completedCount against entriesCount and PATCHes the order to ingestion_complete or
            // audit_started, and jobTracking.jsx decides whether processing is finished. `entryCount`
            // above is the filtered total, so reporting it would have the In Library filter — which
            // excludes status 4, the very status 'completed' counts — conclude that a finished job is
            // back in audit, and write that to the order. Looking at a subset of a job must not be
            // capable of changing the job.
            if (mediaFilter) return null;
            return Promise.all([
                fetchJobTapeStatusCount(cid, jobId, TAPE_AUDIT_COMPLETE_STATUS_IDS),
                fetchJobTapeStatusCount(cid, jobId, TAPE_INGESTION_COMPLETE_STATUS_IDS),
            ]).then(([audited, completed]) => {
                if (onDataLoaded) onDataLoaded({entriesCount: entryCount, auditedCount: audited, completedCount: completed});
            });
        }).catch(error => {
            // The upstream tape data is optional context — a failure here must not take down the
            // whole Job Management view (an unhandled rejection surfaces as the dev error overlay).
            console.error("Error loading tape processing data:", error);
            setTapeData([]);
        });
    };

    // Page one is read live for the same reason the poll is: it is the page that changes. The rest are
    // left cacheable — paging back and forth through settled media should not re-query ArkBridge.
    const loadTapePage = (pageNumber) => {
        fetchJobTapeData(cid, jobId, pageNumber, {...filterOptions, ...(pageNumber === 1 ? {cache: false} : {})})
            .then(listed => {
                // Sorted like every other page, so paging through a job reads as one table rather than as
                // each page in its own arbitrary order.
                const display = orderRowsForDisplay(applyFilterToRows(listed));
                setTapeData(display);
                // Paging back to page one restarts the poll, so leave its baseline describing what is now on
                // screen — otherwise the first tick after coming back commits a render for these same rows.
                // Only page one, because that is the only page the poll compares against.
                if (pageNumber === 1) renderedSignature.current = renderSignature(entriesCount, display);
            })
            .catch(error => {
                console.error("Error loading tape page:", error);
                setTapeData([]);
            });
    };

    const handlePageChange = (event, newPage) => {
        setCurrentPage(newPage);
        loadTapePage(newPage);
    };

    const selectTapeForDetailing = (row) => {
        setSelectedTapeId(row?.item_id);
        setOpenTapeDetails(true);
    };

    return (
        <>
            <TapeDetails open={openTapeDetails} closePopup={() => setOpenTapeDetails(false)} tapeId={selectedTapeId} cid={cid}/>

            <Box padding={2}>
                {showHeading && (
                    <Box className={classes.headingLarge} sx={{textAlign: "left", px: 2, pt: 2}}>
                        Processing
                    </Box>
                )}

                {/*
                  * The full metrics card in place of the two Audit / Ingestion progress bars that used to
                  * sit here.
                  *
                  * Nothing the bars said is lost — Audit/Catalogued carries the audit figure and its bar,
                  * and Virtualized/Uploaded carries the ingestion share as a bar against the catalogued
                  * count — and the card adds the nine counters the bars had no way to show, on the page
                  * where the tapes they describe are listed. (The Percentage tile beside them answers a
                  * different question: how much of the shelved media is still outstanding.)
                  *
                  * Scoped explicitly rather than following the header selector, since this page is about
                  * one job: the card must agree with the table beneath it. Both counts feeding
                  * `onDataLoaded` are still fetched above; they drive the admin status derivation, which is
                  * why removing the bars does not remove the requests behind them.
                  *
                  * No width of its own, so it lines up with the table's edges rather than with the old
                  * bars, which were inset by their label column.
                  */}
                <Box sx={{mt: 2, mb: 2}}>
                    {/* Same interval as the table below, so the counters and the rows never describe two
                        different moments.

                        `onSelectFilter` rather than `orderUuid`: a clicked tile has no link to build here,
                        because the table it would link to is the one directly below it.

                        Held back until there is a scope to count, on the same condition the table's own fetch
                        uses above. This page mounts before either the order or the customer has been fetched —
                        deliberately, so the panel is on screen from the first paint — which leaves cid and
                        jobId briefly undefined. An undefined jobIds is not "no scope" to this card: for its
                        other caller, the dashboard, it means "follow the header job selector". So the card
                        issued its thirteen counts against whatever job the selector last held, then aborted
                        and reissued all thirteen a moment later when the real ids arrived and changed its
                        effect key — twenty-six COUNTs over vwItemsAPI for one page view, the first thirteen
                        of them counting a job the reader is not looking at. The table never had this problem
                        because it was already gated; the card was not, and cannot gate itself. */}
                    {(features.length > 0 && cid && jobId) && (
                        <MediaMetrics
                            cid={cid}
                            jobIds={jobId}
                            refreshIntervalMs={TAPE_REFRESH_INTERVAL_MS}
                            onSelectFilter={applyMediaFilter}
                        />
                    )}
                </Box>

                {/*
                  * What the table is showing, and the way back to the whole job.
                  *
                  * A filtered table that does not say it is filtered is a table that lies: the reader sees
                  * eleven tapes for a job they know holds four hundred and has no way to find out why.
                  * Stated above the table rather than inside it, and clearable in one click, because it was
                  * reached in one click.
                  */}
                {mediaFilter && (
                    <Box sx={{mt: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
                        <Chip
                            icon={<FilterAltIcon/>}
                            label={mediaFilterChipText(mediaFilter)}
                            onDelete={clearMediaFilter}
                            variant="outlined"
                            /*
                             * Colours spelled out because MUI's default chip palette is the light-theme one:
                             * left alone, the funnel and the dismiss cross render near-black on this card and
                             * the only part of the chip anyone can read is the label.
                             */
                            sx={{
                                fontFamily: 'Poppins',
                                fontSize: '12px',
                                maxWidth: '100%',
                                color: 'rgba(255, 255, 255, 0.85)',
                                borderColor: 'rgba(255, 255, 255, 0.24)',
                                backgroundColor: 'rgba(255, 255, 255, 0.04)',
                                '& .MuiChip-icon': {color: 'rgba(255, 255, 255, 0.60)'},
                                '& .MuiChip-deleteIcon': {
                                    color: 'rgba(255, 255, 255, 0.60)',
                                    '&:hover': {color: '#FFFFFF'},
                                },
                            }}
                        />
                    </Box>
                )}

                <Box sx={{mt: 2}}>
                    <DataTable
                        data={tapeData.length > 0 ? tapeData : []}
                        columnsConfig={addClickCallback(TAPE_INGESTION_TABLE_COLUMNS["Standard tapes"], {"Tape Type": selectTapeForDetailing})}
                        dataContainsFormatting={false}
                        showPagination={true}
                        entriesCount={entriesCount}
                        currentPage={currentPage}
                        totalPages={totalPages}
                        handlePageChangeCallback={handlePageChange}
                    />
                </Box>
            </Box>
        </>
    );
};
